<i18n>
{
  "en": {
    "title": "Supply Chain Strategy & Sustainability 2017-2018",
    "description": "We work with sectors that account for the majority of global energy consumption and its corresponding CO<sub>2</sub>, so we’re committed to making sure our own products help reduce energy use and CO<sub>2</sub> emissions. Learn about our Tailored Supply Chain and how we keep our suppliers accountable to health and safety, human rights, ethics, and the environmental standards.",
    "sustainable": {
      "section": "Sustainable Solutions",
      "title": "Supply Chain",
      "supply": {
        "quote1": {
          "text": "Social, environmental, and ethical responsibility for our upstream supply has been one of our key initiatives. Over the last several years, we have led efforts such as ISO 26000 and conflict minerals tracking and contributed to Green Premium and hazardous material compliance.",
          "author": "DK Singh",
          "title": "Chief Procurement Officer, Schneider Electric"
        },
        "txtMedia1": {
          "title": "A responsible and efficient supply chain",
          "para1": "Schneider Electric works with sectors that account for the majority of global energy consumption and its corresponding CO<sub>2</sub>. Therefore, we are especially committed to making sure our own products and solutions help reduce both energy use and CO<sub>2</sub> emissions &mdash; from design to manufacturing to shipping and deployment to products’ end-of-life."
        },
        "txt1": {
          "title": "The new 2018 – 2020 Schneider Sustainability Impact statement includes the following goals:",
          "listItem1": "5 pts / 100 increase in average score of ISO 26000 assessments for our strategic suppliers",
          "listItem2": "300 suppliers under Human Rights &amp; Environment vigilance received specific on-site assessment",
          "listItem3": "100% of sales, procurement, and finance employees trained every year on anti-corruption",
          "para1": "When we share our sustainable development vision with suppliers, we place customers at the center of this message. Schneider Electric’s tailored supply chain is mindful of the diverse practices, standards, and values of the 100+ countries in which we’re present."
        },
        "snapshot1": {
          "title": "Schneider’s supply chain at a glance",
          "cell1": {
            "title": "207",
            "para": "factories in 44 countries"
          },
          "cell2": {
            "title": "86,000",
            "para": "employees"
          },
          "cell3": {
            "title": "€11 billion",
            "para": "purchases"
          },
          "cell4": {
            "title": "98",
            "para": "distribution centers"
          },
          "cell5": {
            "title": "24,000",
            "para": "suppliers"
          },
          "cell6": {
            "title": "150,000",
            "para": "order lines per day"
          },
          "cell7": {
            "title": "#7",
            "para": "Gartner<sup>&trade;</sup> Top 10 Supply Chain Organizations for 2017"
          },
          "cell8": {
            "title": "#17",
            "para": "Gartner<sup>&trade;</sup> Supply Chain Top 25 of 2017"
          }
        },
        "snapshot2": {
          "title": "2017 supply chain highlights",
          "cell1": {
            "title": "358",
            "para": "on-site audits that included sustainability criteria"
          },
          "cell2": {
            "title": "2,400",
            "para": "kilotons of goods shipped"
          },
          "cell3": {
            "title": "88%",
            "para": "of the strategic suppliers passed the third-party evaluation for sustainable development, 2015 – 2017"
          },
          "cell4": {
            "title": "10%",
            "para": "reduction of CO<sub>2</sub> emissions from transportation over 2015 – 2017 period"
          },
          "cell5": {
            "title": "74%",
            "para": "goods shipped by road (23% by ocean, 3% by air)"
          },
          "cell6": {
            "title": "70%",
            "para": "of relevant purchases confirmed as conflict-free under the Conflict Minerals rule of 2012."
          }
        },
        "imageFull": {
          "caption": "Congratulations to Le Vaudreuil plant for its new prize: \"Showcase for Industry of the Future\""
        },
        "txt4": {
          "title": "Industry of the future",
          "para1": "In February 2018, our Le Vaudreuil plant received the \"Showcase for Industry of the Future\" award from the Alliance pour l’Industrie du Futur.",
          "para2": "Schneider’s Le Vaudreuil Smart Factory started this journey in 2017 as a pilot for the company and its TSC 2.0 program, now upgraded to its 4.0 version. Le Vaudreuil has developed and implemented many changes in several areas leveraging our own EcoStruxure<sup>&trade;</sup> solutions such as:",
          "listItem1": "Cybersecurity and the integration of a data bunker",
          "listItem2": "Energy management with the use of our building management system",
          "listItem3": "Addition of sensors to allow predictive maintenance",
          "listItem4": "Combination of EcoStruxure with startup solutions such as apps so employees benefit from continuous training"
        },
        "barometer": {
          "title": "2015 – 2017 Planet <span class='t-nowrap'>& Society</span> barometer achievements",
          "cta": "See the full results",
          "cell1": {
            "title": "Climate",
            "stat": "10.3%",
            "para": "CO<sub>2</sub> savings from transportation"
          },
          "cell2": {
            "title": "Circular Economy",
            "stat": "130",
            "para": "sites received ‘Towards Zero Waste to Landfill’ recognition"
          },
          "cell3": {
            "title": "Ethics",
            "stat": "87.9%",
            "para": "of our recommended suppliers embrace ISO 26000 guidelines"
          }
        },
        "txtMedia2": {
          "title": "Tailored Supply Chain 4.0",
          "para1": "In this digital economy, Schneider Electric’s global supply chain is evolving to become even more tailored, sustainable, and connected with our Tailored Supply Chain 4.0 strategy, powered by EcoStruxure solutions. ",
          "para2": "<strong>Tailored:</strong> We are passionate about our customers’ success, always seeking to provide what they value while leveraging scale and efficiency.",
          "para3": "<strong>Sustainable:</strong> Sustainability is a priority across our supply chain, exemplifying our concern for people and the planet. We optimize resources by developing a circular supply chain with clean and safe facilities powered by renewable energy.",
          "para4": "<strong>Connected:</strong> Our supply chain is digital and smart, powered by EcoStruxure products and solutions. We provide visibility to customers, optimizing end-to-end efficiency and asset reliability."
        },
        "table1": {
          "title": "Tailored, Sustainable, Connected Supply Chain 4.0",
          "row1": {
            "cell1": {
              "title": "11 customer segments with different buying behaviors"
            },
            "cell2": {
              "li1": "Electrical distributors",
              "li2": "IT distributors",
              "li3": "Consumer retailers",
              "li4": "Consumers",
              "li6": "Panel builders",
              "li7": "Repetitive OEMs",
              "li8": "System integrators, large installers, non-repetitive OEMs, EPC",
              "li9": "Small and medium installers",
              "li10": "Utilities",
              "li11": "Other end users",
              "li12": "Web giants"
            }
          },
          "row2": {
            "cell1": {
              "title": "6 digital accelerators"
            },
            "cell2": {
              "p1": "Source",
              "p2": "Make",
              "p3": "Deliver",
              "p4": "Plan",
              "p5": "Care",
              "p6": "Innovate"
            }
          },
          "row3": {
            "cell1": {
              "title": "5 supply chain models"
            },
            "cell2": {
              "p1": "Collaborative",
              "p2": "Lean",
              "p3": "Agile",
              "p4": "Project-driven",
              "p5": "Fully flexible"
            }
          },
          "row4": {
            "cell1": {
              "title": "8 key transformations"
            },
            "cell2": {
              "p1": "End-to-end customer lead times",
              "p2": "Special care units",
              "p3": "SIOP: Sales inventory op planning",
              "p4": "Customer process capabilities",
              "p5": "Network optimization and transportation",
              "p6": "ETO supply chain management",
              "p7": "Field services supply chain",
              "p8": "Supply base management"
            }
          }
        },
        "txt2": {
          "title": "Supplier Relations",
          "para1": "Best supplier in production-related purchases: TS Molymer Co., Ltd.",
          "para2": "Best supplier in non-production purchases: LenovoTM",
          "para3": "Best supplier in logistics: Damco International B.V.",
          "para4": "Competitiveness and productivity: Chuan Shun Electric, Inc. Innovation: NXP Semiconductors",
          "para5": "Quality: Chia Sing Screw Industrial Co. Ltd.",
          "para6": "Social responsibility: Eurostar Engineering Plastics",
          "para7": "Logistics and deliveries: Tianjin Dongming Electronic Industry Co., Ltd.",
          "para8": "Responsiveness: Shubadha Polymers Products Pvt. Ltd."
        },
        "txt3": {
          "title": "Supplier Guide Book",
          "para1": "Our Supplier Guide Book keeps our suppliers aware and accountable to our sustainable development expectations related to health and safety, human rights, ethics, and the environment. This is strengthened by the General Purchasing Terms and Conditions to which all Schneider Electric suppliers must conform."
        },
        "vidEnd": {
          "title": "Supply Chain Progress and Growth",
          "desc": "Annette Clayton, Executive Vice President, Global Supply Chain and North America Operations, Schneider Electric"
        }
      }
    }
  },
  "fr": {
    "title": "Supply Chain Strategy & Sustainability 2017-2018",
    "description": "We work with sectors that account for the majority of global energy consumption and its corresponding CO<sub>2</sub>, so we’re committed to making sure our own products help reduce energy use and CO<sub>2</sub> emissions. Learn about our Tailored Supply Chain and how we keep our suppliers accountable to health and safety, human rights, ethics, and the environmental standards.",
    "sustainable": {
      "section": "Nos Solutions Durables",
      "title": "La Chaîne Logistique",
      "supply": {
        "quote1": {
          "text": "La responsabilité sociale, environementale et éthique de l'amont de notre chaîne logistique est l'une de nos initiatives majeures. Depuis de nombreuses années, nos efforts ont porté sur l’ISO 26000, le suivi des minerais alimentant les conflits et la conformité de nos produits Green Premium.",
          "author": "DK Singh",
          "title": "Directeur des Achats, Schneider Electric"
        },
        "txtMedia1": {
          "title": "Une chaîne logistique responsable et efficace",
          "para1": "Schneider Electric travaille avec des marchés qui concentrent la plus grosse part de la consommation énergétique mondiale et de la production de CO<sub>2</sub> correspondante. Nous nous attachons donc tout particulièrement à nous assurer que nos propres produits et solutions contribuent à réduire à la fois la consommation énergétique et les émissions de CO<sub>2</sub> &mdash; de la conception et de la fabrication à l’expédition et à la distribution et jusqu’à la fin de vie des produits."
        },
        "txt1": {
          "title": "Notre nouveau baromètre Schneider Sustainability Impact pour la période 2018 - 2020 comprend les objectifs suivants : ",
          "listItem1": "5 pts / 100 d’augmentation du score moyen de l’évaluation ISO 26000 pour nos fournisseurs stratégiques",
          "listItem2": "300 fournisseurs sous vigilance sur les droits humains et l’environnement ont reçu une évaluation dédiée sur site",
          "listItem3": "100 % des fonctions ventes, achats et finances formées chaque année à la lutte contre la corruption",
          "para1": "Lorsque nous partageons notre vision du développement durable avec nos fournisseurs, nous plaçons le client au centre de notre message. La chaîne logistique sur mesure de Schneider Electric est respectueuse des diverses pratiques et des valeurs de la centaine de pays dans lesquels nous sommes présents. "
        },
        "snapshot1": {
          "title": "La chaîne logistique en chiffres ",
          "cell1": {
            "title": "207",
            "para": "sites industriels dans 44 pays"
          },
          "cell2": {
            "title": "86 000",
            "para": "collaborateurs"
          },
          "cell3": {
            "title": "€ 11 milliards",
            "para": "d'achats"
          },
          "cell4": {
            "title": "98",
            "para": "centres de distribution"
          },
          "cell5": {
            "title": "24 000",
            "para": "fournisseurs"
          },
          "cell6": {
            "title": "150 000",
            "para": "lignes de commande par jour"
          },
          "cell7": {
            "title": "7ème",
            "para": "dans le palmarès Gartner des 10 meilleurs chaînes logistiques en 2017"
          },
          "cell8": {
            "title": "17 ème ",
            "para": "dans le classement Gartner des 25 meilleures chaînes logistiques"
          }
        },
        "snapshot2": {
          "title": "Faits marquants 2017",
          "cell1": {
            "title": "358",
            "para": "audits sur site intégrant des critères de développement durable"
          },
          "cell2": {
            "title": "2 400",
            "para": "millions de tonnes de marchandises expédiées"
          },
          "cell3": {
            "title": "88 %",
            "para": "des fournisseurs stratégiques ont passé une évaluation indépendante en matière de développement durable entre 2015 et 2017 "
          },
          "cell4": {
            "title": "10 %",
            "para": "de réduction des émissions de CO<sub>2</sub> entre 2015 et 2017"
          },
          "cell5": {
            "title": "74 %",
            "para": "des marchandises acheminées par transport routier (23 % par transport maritime, 3 % par transport aérien)"
          },
          "cell6": {
            "title": "70 %",
            "para": "des achats concernés sont certifiés comme ne provenant pas de zones de conflit, conformément au règlement 2012 sur les minerais alimentant les conflits"
          }
        },
        "imageFull": {
          "caption": "Félicitations à l’usine du Vaudreuil pour son prix : « Vitrine Industrie du futur »"
        },
        "txt4": {
          "title": "Industrie du futur",
          "para1": "En février 2018, notre usine du Vaudreil s’est vu décerner le prix « Vitrine Industrie du futur » par l’Alliance pour l’industrie du futur.",
          "para2": "L'usine intelligente du Vaudreuil s’est lancée dans cette aventure en 2017, dans le cadre d'un projet-pilote pour Schneider Electric et son programme de transformation digitale TSC 2.0, qui en est aujourd'hui à sa version 4.0. Le site a développé et introduit de nombreux changements dans différents domaines, en capitalisant sur nos solutions EcoStruxure :",
          "listItem1": "Cybersécurité et intégration d’un coffre-fort numérique",
          "listItem2": "Gestion de l’énergie grâce à l’utilisation de notre système de gestion du bâtiment",
          "listItem3": "Installation de nouveaux capteurs  pour mettre en place une maintenance prédictive",
          "listItem4": "Combinaison d’EcoStruxure et de solutions développées par des start-up (applications) afin que les collaborateurs bénéficient d'une formation continue"
        },
        "barometer": {
          "title": "Résultats du baromètre Planète & Société 2015-2017 ",
          "cta": "Voir l’ensemble des résultats",
          "cell1": {
            "title": "Climat",
            "stat": "10,3 %",
            "para": "d'économies de CO<sub>2</sub> dans les transports"
          },
          "cell2": {
            "title": "Économie Circulaire",
            "stat": "130",
            "para": "sites labellisés ‘Vers le zéro déchet mis en décharge’"
          },
          "cell3": {
            "title": "Éthique",
            "stat": "87,9 %",
            "para": "de nos fournisseurs recommandés sont en ligne avec la norme ISO 26000"
          }
        },
        "txtMedia2": {
          "title": "Notre chaîne logistique  sur-mesure 4.0",
          "para1": "Dans le cadre de son économie numérique, Schneider Electric a poursuivi l'évolution de sa chaîne logistique afin qu'elle soit plus sur-mesure, plus durable selon sa stratégie \"chaîne logistique sur-mesure 4.0\". Elle est nourrie des solutions EcoStruxure.",
          "para2": "<strong>Sur-mesure :</strong> le succès de nos clients est notre objectif premier et nous recherchons toujours ce qui leur apportera valeur et efficacité.",
          "para3": "<strong>Durable :</strong> la durabilité est une priorité tout au long de la chaîne logistique,  illustration de notre intérêt pour les personnes et la planète. Nous optimisons les ressources en déployant une chaîne logistique circulaire grâce à des installations propres et alimentées par des énergies renouvelables.",
          "para4": "<strong>Connectée :</strong> notre chaîne logistique est numérique et intelligente grâce aux produits et solutions EcoStruxure. Nous assurons transparence à nos clients, optimisation de l'efficacité de bout-en-bout ainsi que fiabilité des actifs."
        },
        "table1": {
          "title": "Notre chaîne logistique 4.0  sur mesure, durable et connectée",
          "row1": {
            "cell1": {
              "title": "11 segments clients avec différents comportements d'achats"
            },
            "cell2": {
              "li1": "Grossistes en électricité",
              "li2": "Distributeurs IT",
              "li3": "Détaillants",
              "li4": "Consommateurs",
              "li6": "Tableautiers",
              "li7": "Constructeurs de machines répétitives",
              "li8": "Systèmes intégrateurs, gros installateurs, constructeurs de machines non répétitives, EPC",
              "li9": "Petits et moyens installateurs",
              "li10": "Régis de distribution",
              "li11": "Autres utilisateurs finaux",
              "li12": "Géants du web"
            }
          },
          "row2": {
            "cell1": {
              "title": "6 accélérateurs numériques"
            },
            "cell2": {
              "p1": "Sourcer",
              "p2": "Faire",
              "p3": "Fabriquer",
              "p4": "Planifier",
              "p5": "Prendre soin",
              "p6": "Innover"
            }
          },
          "row3": {
            "cell1": {
              "title": "5 modèles de chaîne logistique "
            },
            "cell2": {
              "p1": "Collaborative",
              "p2": "Lean ",
              "p3": "Agile",
              "p4": "Destinée aux projets",
              "p5": "Totalement flexible"
            }
          },
          "row4": {
            "cell1": {
              "title": "8 transformations majeures"
            },
            "cell2": {
              "p1": "Délais de livraison clients",
              "p2": "Unités spéciales",
              "p3": "SIOP",
              "p4": "Processus",
              "p5": "ETO gestion de la chaîne logistique",
              "p6": "Chaîne logistique services sur le terrain",
              "p7": "Gestion de la base d'approvisionnement"
            }
          }
        },
        "txt3": {
          "title": "Supplier Guide Book",
          "para1": "Le Supplier Guide Book, notre charte dédiée aux fournisseurs, permet à ces derniers de garder à l’esprit et de respecter nos attentes en matière de développement durable dans quatre grands domaines : santé et sécurité, droits de l’homme, éthique et environnement. Cette démarche est renforcée par les conditions générales d’achats, auxquelles tout fournisseur doit se conformer. "
        },
        "vidEnd": {
          "title": "Les progrès de notre chaîne logistique et la croissance",
          "desc": "Annette Clayton, Directeur général Opérations industrielles et Opérations Amérique du Nord, Schneider Electric"
        }
      }
    }
  }
}
</i18n>

<template>
  <div id="">
    <Hero :loResSrc="require('../../assets/img/sustainable/sustainable-solutions-supply-chain-hero-lowres.jpg')" :hiResSrc="require('../../assets/img/sustainable/sustainable-solutions-supply-chain-hero.jpg')" chapter="02" :section="$t('sustainable.section')" :title="$t('sustainable.title')" :grayBG="true" />

    <section class="m-t-120 m-b-120">
      <YouTubeVideo videoId="gKyAC_7pV40" :title="$t('sustainable.supply.vidEnd.title')" :description="$t('sustainable.supply.vidEnd.desc')" :preview="require('@/assets/img/thumbnails/SSDHighlightVideo_AnnetteClayton_Still.jpg')"/>
    </section>

    <section class="m-b-120 m-t-120 l-section-pad l-bg-white">
      <TxtMedia>
        <h2 id="efficientsupplychain" class="m-b-45" slot="main-content" v-html="$t('sustainable.supply.txtMedia1.title')"></h2>
        <p slot="main-content" v-html="$t('sustainable.supply.txtMedia1.para1')"></p>
        <p slot="main-content" v-html="$t('sustainable.supply.txt1.title')"></p>
        <ul slot="main-content">
          <li v-html="$t('sustainable.supply.txt1.listItem1')"></li>
          <li v-html="$t('sustainable.supply.txt1.listItem2')"></li>
          <li v-html="$t('sustainable.supply.txt1.listItem3')"></li>
        </ul>
        <p slot="main-content" v-html="$t('sustainable.supply.txt1.para1')"></p>
        <img slot="sidebar" src="../../assets/img/sustainable/sustainable-solutions-supply-chain-responsible-efficient.jpg" />
      </TxtMedia>
    </section>

    <section class="m-b-120 m-t-120">
      <Snapshot>
        <div slot="above">
          <h2 id="supplychainataglance" class="t-grey" v-html="$t('sustainable.supply.snapshot1.title')"></h2>
        </div>
        <SnapshotCard class="t-white" :bgimg="require('@/assets/img/sustainable/SaoPauloJoao0256.jpg')">
          <h4 class="" v-html="$t('sustainable.supply.snapshot1.cell1.title')"></h4>
          <div class="t-hr"></div>
          <p v-html="$t('sustainable.supply.snapshot1.cell1.para')"></p>
        </SnapshotCard>
        <SnapshotCard>
          <h4 class="t-green-light" v-html="$t('sustainable.supply.snapshot1.cell2.title')"></h4>
          <div class="t-hr"></div>
          <p v-html="$t('sustainable.supply.snapshot1.cell2.para')"></p>
        </SnapshotCard>
        <SnapshotCard class="t-white" bgc="#3dcd58">
          <h4 class="" v-html="$t('sustainable.supply.snapshot1.cell3.title')"></h4>
          <div class="t-hr t-hr--white"></div>
          <p v-html="$t('sustainable.supply.snapshot1.cell3.para')"></p>
        </SnapshotCard>
        <SnapshotCard>
          <h4 class="t-green-light" v-html="$t('sustainable.supply.snapshot1.cell4.title')"></h4>
          <div class="t-hr"></div>
          <p v-html="$t('sustainable.supply.snapshot1.cell4.para')"></p>
        </SnapshotCard>
        <SnapshotCard>
          <h4 class="t-green-light" v-html="$t('sustainable.supply.snapshot1.cell5.title')"></h4>
          <div class="t-hr"></div>
          <p v-html="$t('sustainable.supply.snapshot1.cell5.para')"></p>
        </SnapshotCard>
        <SnapshotCard class="t-white" bgc="#3dcd58">
          <h4 class="" v-html="$t('sustainable.supply.snapshot1.cell6.title')"></h4>
          <div class="t-hr t-hr--white"></div>
          <p v-html="$t('sustainable.supply.snapshot1.cell6.para')"></p>
        </SnapshotCard>
        <SnapshotCard class="t-white" :bgimg="require('@/assets/img/sustainable/GettyImages-170998531.jpg')">
          <h4 v-html="$t('sustainable.supply.snapshot1.cell7.title')"></h4>
          <div class="t-hr"></div>
          <p v-html="$t('sustainable.supply.snapshot1.cell7.para')"></p>
        </SnapshotCard>
        <SnapshotCard class="t-white" bgc="#3dcd58">
          <h4 class="" v-html="$t('sustainable.supply.snapshot1.cell8.title')"></h4>
          <div class="t-hr t-hr--white"></div>
          <p v-html="$t('sustainable.supply.snapshot1.cell8.para')"></p>
        </SnapshotCard>
      </Snapshot>
    </section>

    <section class="m-b-120 m-t-120">
      <Snapshot>
        <div slot="above">
          <h2 id="supplychainhighlights" class="t-grey" v-html="$t('sustainable.supply.snapshot2.title')"></h2>
        </div>
        <SnapshotCard class="t-white" :double="true" :bgimg="require('../../assets/img/sustainable/sustainable-solutions-supply-chain-2017-supply-chain-cell1.jpg')">
          <h4 v-html="$t('sustainable.supply.snapshot2.cell1.title')"></h4>
          <div class="t-hr"></div>
          <p v-html="$t('sustainable.supply.snapshot2.cell1.para')"></p>
        </SnapshotCard>
        <SnapshotCard>
          <h4 class="t-green-light" v-html="$t('sustainable.supply.snapshot2.cell2.title')"></h4>
          <div class="t-hr"></div>
          <p v-html="$t('sustainable.supply.snapshot2.cell2.para')"></p>
        </SnapshotCard>
        <SnapshotCard class="t-white" bgc="#3dcd58">
          <h4 class="" v-html="$t('sustainable.supply.snapshot2.cell3.title')"></h4>
          <div class="t-hr t-hr--white"></div>
          <p v-html="$t('sustainable.supply.snapshot2.cell3.para')"></p>
        </SnapshotCard>
        <SnapshotCard class="t-white" bgc="#3dcd58">
          <h4 class="" v-html="$t('sustainable.supply.snapshot2.cell4.title')"></h4>
          <div class="t-hr t-hr--white"></div>
          <p v-html="$t('sustainable.supply.snapshot2.cell4.para')"></p>
        </SnapshotCard>
        <SnapshotCard>
          <h4 class="t-green-light" v-html="$t('sustainable.supply.snapshot2.cell5.title')"></h4>
          <div class="t-hr"></div>
          <p v-html="$t('sustainable.supply.snapshot2.cell5.para')"></p>
        </SnapshotCard>
        <SnapshotCard :double="true">
          <h4 class="t-green-light" v-html="$t('sustainable.supply.snapshot2.cell6.title')"></h4>
          <div class="t-hr"></div>
          <p v-html="$t('sustainable.supply.snapshot2.cell6.para')"></p>
        </SnapshotCard>
      </Snapshot>
    </section>

    <section class="m-b-120 m-t-120 l-section-pad l-bg-white">
      <ImageFull class="m-b-50" :caption="$t('sustainable.supply.imageFull.caption')">
        <img src="../../assets/img/sustainable/sustainable-solutions-supply-chain-industry-future.jpg" />
      </ImageFull>
      <Txt>
        <h2 id="industryofthefuture" v-html="$t('sustainable.supply.txt4.title')"></h2>
        <p v-html="$t('sustainable.supply.txt4.para1')"></p>
        <p v-html="$t('sustainable.supply.txt4.para2')"></p>
        <ul>
          <li v-html="$t('sustainable.supply.txt4.listItem1')"></li>
          <li v-html="$t('sustainable.supply.txt4.listItem2')"></li>
          <li v-html="$t('sustainable.supply.txt4.listItem3')"></li>
          <li v-html="$t('sustainable.supply.txt4.listItem4')"></li>
        </ul>
      </Txt>
    </section>

    <section class="m-b-120 m-t-120">
      <Barometer>
        <h2 class="t-grey" slot="copy" v-html="$t('sustainable.supply.barometer.title')"></h2>

        <CTA slot="copy" href="/static/PSBarometer-Q42017_results.pdf" :buttonText="$t('sustainable.supply.barometer.cta')" target="_blank"/>
        <BarometerIndicator class="col-xs-18 col-sm-9" :color="$root.sassVars.cGreenLight" :title="$t('sustainable.supply.barometer.cell1.title')" :stat="$t('sustainable.supply.barometer.cell1.stat')" :body="$t('sustainable.supply.barometer.cell1.para')"></BarometerIndicator>
        <BarometerIndicator class="col-xs-18 col-sm-9" :color="$root.sassVars.cGreenLight" :title="$t('sustainable.supply.barometer.cell2.title')" :stat="$t('sustainable.supply.barometer.cell2.stat')" :body="$t('sustainable.supply.barometer.cell2.para')"></BarometerIndicator>
        <BarometerIndicator class="col-xs-18" color="#009530" :title="$t('sustainable.supply.barometer.cell3.title')" :stat="$t('sustainable.supply.barometer.cell3.stat')" :body="$t('sustainable.supply.barometer.cell3.para')"></BarometerIndicator>
      </Barometer>
    </section>

    <hr>

    <section class="m-b-120 m-t-120">
      <imageFull class="m-b-80">
        <img src="../../assets/img/sustainable/sustainable-solutions-supply-chain-tailored-supply-chain.jpg" />
      </imageFull>

      <Txt>
        <h2 id="supplychain4.0" class="m-b-45" v-html="$t('sustainable.supply.txtMedia2.title')"></h2>
        <p v-html="$t('sustainable.supply.txtMedia2.para1')"></p>
        <p v-html="$t('sustainable.supply.txtMedia2.para2')"></p>
        <p v-html="$t('sustainable.supply.txtMedia2.para3')"></p>
        <p v-html="$t('sustainable.supply.txtMedia2.para4')"></p>
      </Txt>
    </section>

    <section class="l-bg-white l-section-pad">
      <Table class="m-b-45" columns="2">
        <div slot="above">
          <h2 v-html="$t('sustainable.supply.table1.title')"></h2>
        </div>
        <TableRow>
          <TableCell bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('sustainable.supply.table1.row1.cell1.title')"></h3>
          </TableCell>
          <TableCell>
            <ul>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li1')"></li>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li2')"></li>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li3')"></li>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li4')"></li>
              <!-- <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li5')"></li> -->
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li6')"></li>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li7')"></li>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li8')"></li>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li9')"></li>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li10')"></li>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li11')"></li>
              <li class="t-chartbody" v-html="$t('sustainable.supply.table1.row1.cell2.li12')"></li>
            </ul>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 v-html="$t('sustainable.supply.table1.row2.cell1.title')"></h3>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/sustainable/GettyImages-726797925.jpg')">
            <ul>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row2.cell2.p1')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row2.cell2.p2')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row2.cell2.p3')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row2.cell2.p4')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row2.cell2.p5')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row2.cell2.p6')"></li>
            </ul>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('sustainable.supply.table1.row3.cell1.title')"></h3>
          </TableCell>
          <TableCell>
            <ul>
              <li class="t-chartbody m-b-0" v-html="$t('sustainable.supply.table1.row3.cell2.p1')"></li>
              <li class="t-chartbody m-b-0" v-html="$t('sustainable.supply.table1.row3.cell2.p2')"></li>
              <li class="t-chartbody m-b-0" v-html="$t('sustainable.supply.table1.row3.cell2.p3')"></li>
              <li class="t-chartbody m-b-0" v-html="$t('sustainable.supply.table1.row3.cell2.p4')"></li>
              <li class="t-chartbody m-b-0" v-html="$t('sustainable.supply.table1.row3.cell2.p5')"></li>
            </ul>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 v-html="$t('sustainable.supply.table1.row4.cell1.title')"></h3>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/sustainable/GettyImages-726798417.jpg')">
            <ul>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row4.cell2.p1')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row4.cell2.p2')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row4.cell2.p3')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row4.cell2.p4')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row4.cell2.p5')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row4.cell2.p6')"></li>
              <li class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row4.cell2.p7')"></li>
              <li v-if="$i18n.locale === 'en'" class="t-chartbody t-white m-b-0" v-html="$t('sustainable.supply.table1.row4.cell2.p8')"></li>
            </ul>
          </TableCell>
        </TableRow>
      </Table>
    </section>

    <!-- <section class="m-b-120 m-t-120">
      <Txt>
        <h2 v-html="$t('sustainable.supply.txt2.title')"></h2>
        <p v-html="$t('sustainable.supply.txt2.para1')"></p>
        <p v-html="$t('sustainable.supply.txt2.para2')"></p>
        <p v-html="$t('sustainable.supply.txt2.para3')"></p>
        <p v-html="$t('sustainable.supply.txt2.para4')"></p>
        <p v-html="$t('sustainable.supply.txt2.para5')"></p>
        <p v-html="$t('sustainable.supply.txt2.para6')"></p>
        <p v-html="$t('sustainable.supply.txt2.para7')"></p>
        <p v-html="$t('sustainable.supply.txt2.para8')"></p>
      </Txt>
    </section> -->

    <section class="m-b-120 m-t-120">
      <Txt class="">
        <h2 id="supplierguidebook" v-html="$t('sustainable.supply.txt3.title')"></h2>
        <p v-html="$t('sustainable.supply.txt3.para1')"></p>
      </Txt>
    </section>

    <section class="m-b-80">
      <Quote  :photo="require('@/assets/img/sustainable/dk-singh-rgb.jpg')" :name="$t('sustainable.supply.quote1.author')" :title="$t('sustainable.supply.quote1.title')">
        <div v-html="$t('sustainable.supply.quote1.text')"></div>
      </Quote>
    </section>

    <div class="l-content">
      <FooterNav
        nextRoute="OurCustomers_Overview"
        prevRoute="SustainableSolutions_EnergyUniversity" />
    </div>

  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import Quote from '@/components/Quote'
import Statement from '@/components/Statement'
import FourCols from '@/components/FourCols'
import FourColsItem from '@/components/FourColsItem'
import CTA from '@/components/CTA'
import TxtMedia from '@/components/TxtMedia'
import Txt from '@/components/Txt'
import Snapshot from '@/components/Snapshot'
import SnapshotCard from '@/components/SnapshotCard'
import ImageFull from '@/components/ImageFull'
import Barometer from '@/components/Barometer'
import BarometerIndicator from '@/components/BarometerIndicator'
import Table from '@/components/Table'
import TableRow from '@/components/TableRow'
import TableCell from '@/components/TableCell'
import YouTubeVideo from '@/components/YouTubeVideo'

export default {
  name: 'Introduction_Welcome',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    Quote,
    Statement,
    FourCols,
    FourColsItem,
    Snapshot,
    SnapshotCard,
    TxtMedia,
    CTA,
    ImageFull,
    Txt,
    Barometer,
    BarometerIndicator,
    Table,
    TableRow,
    TableCell,
    YouTubeVideo
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/sustainable-solutions/supply-chain.vue